import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const BASE = `${config.BASE_URL}/tournaments-control`;

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function assertOk(res, data, fallbackMessage) {
  if (!res.ok) throw new Error(data.message || fallbackMessage);
}

export const fetchSportsList = async ({ scope } = {}) => {
  const qs =
    scope === "master" ? "?scope=master" : "";
  const res = await authFetch(`${BASE}/sports${qs}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to fetch sports list");
  return data;
};

export const createSport = async (payload, csrfToken) => {
  const res = await authFetch(`${BASE}/sports`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to create sport");
  return data;
};

export const updateSport = async (id, payload, csrfToken) => {
  const res = await authFetch(
    `${BASE}/sports/${encodeURIComponent(String(id))}`,
    {
      method: "PUT",
      credentials: "include",
      headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      body: JSON.stringify(payload),
    },
  );
  const data = await parseJson(res);
  assertOk(res, data, "Failed to update sport");
  return data;
};

export const deleteSport = async (id, csrfToken) => {
  const res = await authFetch(
    `${BASE}/sports/${encodeURIComponent(String(id))}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
  );
  const data = await parseJson(res);
  assertOk(res, data, "Failed to delete sport");
  return data;
};

/** Admin dashboard statistics */
export const fetchTournamentDashboard = async () => {
  const res = await authFetch(`${BASE}/dashboard`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to load tournament dashboard");
  return data;
};

export const getTournamentLookups = async () => {
  const res = await authFetch(`${BASE}/lookups`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to load tournament lookups");
  return data;
};

export const fetchTournamentsList = async (params = {}) => {
  const q = new URLSearchParams();
  if (params.name) q.set("name", params.name);
  const qs = q.toString();
  const res = await authFetch(`${BASE}${qs ? `?${qs}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to fetch tournaments");
  return data;
};

export const createTournament = async (payload, csrfToken) => {
  const res = await authFetch(BASE, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    const err = new Error(data.message || "Failed to create tournament");
    err.errors = data.errors;
    err.status = res.status;
    throw err;
  }
  return data;
};

export const updateTournament = async (id, payload, csrfToken) => {
  const res = await authFetch(`${BASE}/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    const err = new Error(data.message || "Failed to update tournament");
    err.errors = data.errors;
    err.status = res.status;
    throw err;
  }
  return data;
};

export const deleteTournament = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to remove tournament");
  return data;
};

/** mst_tournament_levels */
export const fetchTournamentLevelsList = async () => {
  const res = await authFetch(`${BASE}/levels`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to fetch tournament levels");
  return data;
};

export const createTournamentLevel = async (payload, csrfToken) => {
  const res = await authFetch(`${BASE}/levels`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to create level");
  return data;
};

export const updateTournamentLevel = async (id, payload, csrfToken) => {
  const res = await authFetch(`${BASE}/levels/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to update level");
  return data;
};

export const deleteTournamentLevel = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/levels/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to delete level");
  return data;
};

/** mst_tournament_updates */
export const fetchTournamentUpdatesList = async () => {
  const res = await authFetch(`${BASE}/updates`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to fetch tournament updates");
  return data;
};

export const createTournamentUpdate = async (payload, csrfToken) => {
  const res = await authFetch(`${BASE}/updates`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to create update");
  return data;
};

export const updateTournamentUpdate = async (id, payload, csrfToken) => {
  const res = await authFetch(`${BASE}/updates/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to update");
  return data;
};

export const deleteTournamentUpdate = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/updates/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to delete update");
  return data;
};

/** mst_tournament_result_types (Winner, Runner-up, etc.) */
export const fetchTournamentResultTypesList = async () => {
  const res = await authFetch(`${BASE}/result-types`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to fetch result types");
  return data;
};

export const createTournamentResultType = async (payload, csrfToken) => {
  const res = await authFetch(`${BASE}/result-types`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to create result type");
  return data;
};

export const updateTournamentResultType = async (id, payload, csrfToken) => {
  const res = await authFetch(`${BASE}/result-types/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to update result type");
  return data;
};

export const deleteTournamentResultType = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/result-types/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to delete result type");
  return data;
};

/** Admin: approvals + certificates */
export const fetchAdminRegistrationsApprovalList = async (params = {}) => {
  const q = new URLSearchParams();
  if (params.student_search) q.set("student_search", params.student_search);
  if (params.tournament_id) q.set("tournament_id", String(params.tournament_id));
  if (params.payment_verified !== undefined && params.payment_verified !== "") {
    q.set("payment_verified", String(params.payment_verified));
  }
  if (params.registration_verified !== undefined && params.registration_verified !== "") {
    q.set("registration_verified", String(params.registration_verified));
  }
  if (params.date_from) q.set("date_from", params.date_from);
  if (params.date_to) q.set("date_to", params.date_to);
  const qs = q.toString();
  const res = await authFetch(`${BASE}/registrations/approval-list${qs ? `?${qs}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to load registrations");
  return data;
};

export const fetchAdminResultsApprovalList = async (params = {}) => {
  const q = new URLSearchParams();
  if (params.student_search) q.set("student_search", params.student_search);
  if (params.tournament_id) q.set("tournament_id", String(params.tournament_id));
  if (params.result_verified !== undefined && params.result_verified !== "") {
    q.set("result_verified", String(params.result_verified));
  }
  const qs = q.toString();
  const res = await authFetch(`${BASE}/results/approval-list${qs ? `?${qs}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Failed to load tournament results");
  return data;
};

export const bulkVerifyRegistrations = async (registrationIds, csrfToken) => {
  const res = await authFetch(`${BASE}/registrations/bulk-verify`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ registration_ids: registrationIds }),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Bulk verify failed");
  return data;
};

export const setAdminPaymentVerification = async (paymentId, paymentVerified, csrfToken) => {
  const res = await authFetch(`${BASE}/registrations/set-payment-verified`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ payment_id: paymentId, payment_verified: paymentVerified }),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Could not update payment verification");
  return data;
};

export const bulkSetAdminPaymentVerification = async (paymentIds, paymentVerified, csrfToken) => {
  const res = await authFetch(`${BASE}/registrations/set-payment-verified-bulk`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ payment_ids: paymentIds, payment_verified: paymentVerified }),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Bulk payment verification failed");
  return data;
};

export const bulkApplyRegistrationCertificates = async (payload, csrfToken) => {
  const res = await authFetch(`${BASE}/registrations/bulk-certificate`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Certificate update failed");
  return data;
};

export const bulkVerifyTournamentResults = async (resultIds, csrfToken) => {
  const res = await authFetch(`${BASE}/results/bulk-verify`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ result_ids: resultIds }),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Bulk verify failed");
  return data;
};

export const bulkSetResultCertificateTemplate = async (resultIds, certificateId, csrfToken) => {
  const res = await authFetch(`${BASE}/results/bulk-certificate-template`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ result_ids: resultIds, certificate_id: certificateId }),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Certificate template update failed");
  return data;
};

export const bulkSetResultCertificateStatus = async (resultIds, certificateStatus, csrfToken) => {
  const res = await authFetch(`${BASE}/results/bulk-certificate-status`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ result_ids: resultIds, certificate_status: certificateStatus }),
  });
  const data = await parseJson(res);
  assertOk(res, data, "Certificate status update failed");
  return data;
};
